/**
 * VisualBuilderAssetsPage - Admin UI for managing visual builder images
 * 
 * Manages images and content for the visual product configurator.
 * Organized by category with full CRUD functionality.
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Header } from '../components/Header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';
import { API_BASE } from '../lib/apiBase';
import ProductFamilyTabs, { getProductFamilyMeta } from '../components/ProductFamilyTabs';
import VBQuantityPresetsCard from '../components/VBQuantityPresetsCard';
import {
    Image,
    Plus,
    Edit,
    Trash2,
    Loader2,
    ArrowLeft,
    GripVertical,
    Eye,
    EyeOff,
    ExternalLink,
    RefreshCw,
    Upload,
    Download,
    AlertCircle,
    CheckCircle2,
    Disc,
    Package,
    Layers,
    Sparkles
} from 'lucide-react';

const API = API_BASE;

// Icon mapping for categories (frontend maps string keys to components)
const ICON_MAP = {
    disc: Disc,
    package: Package,
    layers: Layers,
    palette: Disc,  // Using Disc as palette substitute
    sparkles: Sparkles,
    plus: Plus,
    image: Image,
};

const getIconComponent = (iconKey) => ICON_MAP[iconKey] || Layers;

export default function VisualBuilderAssetsPage() {
    const { isAdmin } = useAuth();
    const navigate = useNavigate();
    
    const [assets, setAssets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeCategory, setActiveCategory] = useState(null);
    // Phase 2: family scoping
    const [productFamily, setProductFamily] = useState('vinyl');
    const [familyCounts, setFamilyCounts] = useState({ vinyl: 0, cd: 0, dvd: 0, bluray: 0 });
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [showPreview, setShowPreview] = useState(null);
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    
    // Form state
    const [formData, setFormData] = useState({
        category_id: '',
        option_id: '',
        image_url: '',
        s3_key: '',
        label: '',
        short_description: '',
        why_it_matters: '',
        display_order: 0,
        is_active: true
    });
    const [categoryFormData, setCategoryFormData] = useState({
        category_id: '',
        label: '',
        description: '',
        icon: 'layers',
        ui_type: 'cards',
        allow_multiple: false,
        display_order: 0
    });
    const [editingAsset, setEditingAsset] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // Redirect non-admins
    useEffect(() => {
        if (!isAdmin) {
            navigate('/dashboard');
        }
    }, [isAdmin, navigate]);

    // Load categories and assets
    useEffect(() => {
        fetchCategories();
        fetchAssets();
        fetchFamilyCounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productFamily]);

    const fetchFamilyCounts = async () => {
        const families = ['vinyl', 'cd', 'dvd', 'bluray'];
        try {
            const token = localStorage.getItem('token');
            const results = await Promise.all(
                families.map(fam =>
                    axios.get(`${API}/api/visual-builder/categories?product_family=${fam}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    })
                        .then(r => [fam, (r.data || []).length])
                        .catch(() => [fam, 0])
                )
            );
            setFamilyCounts(Object.fromEntries(results));
        } catch (e) {
            console.error('Failed to fetch family counts:', e);
        }
    };

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API}/api/visual-builder/categories?product_family=${encodeURIComponent(productFamily)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const cats = response.data || [];
            setCategories(cats);
            // Set active category to first one in this family
            if (cats.length > 0) {
                setActiveCategory(cats[0].category_id);
            } else {
                setActiveCategory(null);
            }
        } catch (error) {
            console.error('Failed to load categories:', error);
            // Fallback to empty - migration might not be done yet
        }
    };

    const fetchAssets = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${API}/api/visual-builder/assets?active_only=false&product_family=${encodeURIComponent(productFamily)}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setAssets(response.data || []);
        } catch (error) {
            toast.error('Failed to load assets');
            console.error(error);
        } finally {
            setIsLoading(false);
        }
    };

    const getAssetsByCategory = (categoryId) => {
        return assets
            .filter(a => (a.category_id || a.category) === categoryId)
            .sort((a, b) => a.display_order - b.display_order);
    };

    const handleCreate = async () => {
        if (!formData.option_id.trim() || !formData.label.trim()) {
            toast.error('Option ID and Label are required');
            return;
        }
        
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(`${API}/api/visual-builder/admin/assets`, {
                ...formData,
                category_id: formData.category_id || activeCategory,
                // Phase 2: scope new asset to currently-active family
                product_family: productFamily,
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Asset created');
            setShowCreateModal(false);
            resetForm();
            fetchAssets();
            fetchCategories(); // Refresh counts
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create asset');
        } finally {
            setIsSaving(false);
        }
    };

    const handleEdit = (asset) => {
        setEditingAsset(asset);
        setFormData({
            category_id: asset.category_id || asset.category,
            option_id: asset.option_id,
            image_url: asset.image_url || '',
            s3_key: asset.s3_key || '',
            label: asset.label || '',
            short_description: asset.short_description || '',
            why_it_matters: asset.why_it_matters || '',
            display_order: asset.display_order || 0,
            is_active: asset.is_active !== false
        });
        setShowEditModal(true);
    };

    const handleUpdate = async () => {
        if (!formData.label.trim()) {
            toast.error('Label is required');
            return;
        }
        
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/api/visual-builder/admin/assets/${editingAsset.id}`, {
                image_url: formData.image_url,
                s3_key: formData.s3_key,
                label: formData.label,
                short_description: formData.short_description,
                why_it_matters: formData.why_it_matters,
                display_order: parseInt(formData.display_order) || 0,
                is_active: formData.is_active
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Asset updated');
            setShowEditModal(false);
            resetForm();
            fetchAssets();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update asset');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (assetId) => {
        setIsDeleting(true);
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/api/visual-builder/admin/assets/${assetId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Asset deleted');
            setShowDeleteConfirm(null);
            fetchAssets();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete asset');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleToggleActive = async (asset) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/api/visual-builder/admin/assets/${asset.id}`, {
                is_active: !asset.is_active
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(asset.is_active ? 'Asset hidden' : 'Asset shown');
            fetchAssets();
        } catch (error) {
            toast.error('Failed to toggle visibility');
        }
    };

    const resetForm = () => {
        setFormData({
            category_id: activeCategory,
            option_id: '',
            image_url: '',
            s3_key: '',
            label: '',
            short_description: '',
            why_it_matters: '',
            display_order: getAssetsByCategory(activeCategory).length,
            is_active: true
        });
        setEditingAsset(null);
    };

    const openCreateModal = () => {
        resetForm();
        setShowCreateModal(true);
    };

    const getCategoryConfig = (categoryId) => {
        const cat = categories.find(c => c.category_id === categoryId);
        if (cat) {
            return {
                id: cat.category_id,
                label: cat.label,
                description: cat.description,
                icon: getIconComponent(cat.icon),
                asset_count: cat.asset_count || 0
            };
        }
        return { id: categoryId, label: categoryId, icon: Layers, asset_count: 0 };
    };

    // Category CRUD handlers
    const handleCreateCategory = async () => {
        if (!categoryFormData.category_id.trim() || !categoryFormData.label.trim()) {
            toast.error('Category ID and Label are required');
            return;
        }
        
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.post(
                `${API}/api/visual-builder/admin/categories`,
                { ...categoryFormData, product_family: productFamily },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Category created');
            setShowCategoryModal(false);
            resetCategoryForm();
            fetchCategories();
            fetchFamilyCounts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to create category');
        } finally {
            setIsSaving(false);
        }
    };

    const handleUpdateCategory = async () => {
        if (!categoryFormData.label.trim()) {
            toast.error('Label is required');
            return;
        }
        
        setIsSaving(true);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${API}/api/visual-builder/admin/categories/${editingCategory.category_id}`, {
                label: categoryFormData.label,
                description: categoryFormData.description,
                icon: categoryFormData.icon,
                ui_type: categoryFormData.ui_type,
                allow_multiple: categoryFormData.allow_multiple,
                display_order: categoryFormData.display_order
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Category updated');
            setShowCategoryModal(false);
            resetCategoryForm();
            fetchCategories();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update category');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteCategory = async (categoryId) => {
        if (!window.confirm('Are you sure you want to delete this category? This cannot be undone.')) {
            return;
        }
        
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/api/visual-builder/admin/categories/${categoryId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Category deleted');
            if (activeCategory === categoryId && categories.length > 1) {
                setActiveCategory(categories.find(c => c.category_id !== categoryId)?.category_id);
            }
            fetchCategories();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to delete category');
        }
    };

    const resetCategoryForm = () => {
        setCategoryFormData({
            category_id: '',
            label: '',
            description: '',
            icon: 'layers',
            ui_type: 'cards',
            allow_multiple: false,
            display_order: categories.length
        });
        setEditingCategory(null);
    };

    const openEditCategoryModal = (cat) => {
        setEditingCategory(cat);
        setCategoryFormData({
            category_id: cat.category_id,
            label: cat.label,
            description: cat.description || '',
            icon: cat.icon || 'layers',
            ui_type: cat.ui_type || 'cards',
            allow_multiple: cat.allow_multiple || false,
            display_order: cat.display_order || 0
        });
        setShowCategoryModal(true);
    };

    const openCreateCategoryModal = () => {
        resetCategoryForm();
        setShowCategoryModal(true);
    };

    // Stats
    const totalAssets = assets.length;
    const activeAssets = assets.filter(a => a.is_active !== false).length;
    const assetsWithImages = assets.filter(a => a.image_url).length;

    if (!isAdmin) return null;

    return (
        <div className="min-h-screen bg-background">
            <Header />
            
            <div className="max-w-7xl mx-auto px-4 py-6">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-4">
                        <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')}>
                            <ArrowLeft className="h-4 w-4 mr-1" />
                            Back
                        </Button>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Visual Builder Assets</h1>
                            <p className="text-sm text-slate-500">
                                Manage images and content for the visual product configurator
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={fetchAssets}>
                            <RefreshCw className="h-4 w-4 mr-1" />
                            Refresh
                        </Button>
                        <Button onClick={openCreateModal} data-testid="create-asset-btn">
                            <Plus className="h-4 w-4 mr-1" />
                            Add Asset
                        </Button>
                    </div>
                </div>

                {/* Phase 2: Product family tabs */}
                <div className="mb-4 flex items-center justify-between">
                    <ProductFamilyTabs
                        value={productFamily}
                        onChange={setProductFamily}
                        counts={familyCounts}
                    />
                    <span className="text-xs text-slate-500">
                        Showing <span className="font-medium text-slate-700">{getProductFamilyMeta(productFamily).label}</span> assets
                    </span>
                </div>

                {/* Admin-configurable customer quantity presets for the
                    Guided Setup "How many copies?" step. Scoped to the
                    currently-selected product family so admins can set
                    different presets per family. */}
                <div className="mb-4">
                    <VBQuantityPresetsCard productFamily={productFamily} />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                    <Card className="bg-white">
                        <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Total Assets</p>
                                    <p className="text-2xl font-bold">{totalAssets}</p>
                                </div>
                                <Image className="h-8 w-8 text-slate-300" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white">
                        <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Active</p>
                                    <p className="text-2xl font-bold text-emerald-600">{activeAssets}</p>
                                </div>
                                <CheckCircle2 className="h-8 w-8 text-emerald-200" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white">
                        <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">With Images</p>
                                    <p className="text-2xl font-bold text-blue-600">{assetsWithImages}</p>
                                </div>
                                <Image className="h-8 w-8 text-blue-200" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="bg-white">
                        <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-slate-500">Categories</p>
                                    <p className="text-2xl font-bold text-purple-600">{categories.length}</p>
                                </div>
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="p-1"
                                    onClick={openCreateCategoryModal}
                                    title="Add Category"
                                >
                                    <Plus className="h-5 w-5 text-purple-400 hover:text-purple-600" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Main Content with Tabs */}
                <Card>
                    <Tabs value={activeCategory || ''} onValueChange={setActiveCategory}>
                        <CardHeader className="pb-0">
                            <div className="flex items-center justify-between mb-2">
                                <TabsList className="w-auto justify-start flex-wrap h-auto gap-1 bg-slate-100 p-1">
                                    {categories.map(cat => {
                                        const count = getAssetsByCategory(cat.category_id).length;
                                        const Icon = getIconComponent(cat.icon);
                                        return (
                                            <TabsTrigger 
                                                key={cat.category_id} 
                                                value={cat.category_id}
                                                className="data-[state=active]:bg-white"
                                                data-testid={`tab-${cat.category_id}`}
                                            >
                                                <Icon className="h-4 w-4 mr-1.5" />
                                                {cat.label}
                                                <Badge variant="secondary" className="ml-1.5 text-xs">
                                                {count}
                                            </Badge>
                                        </TabsTrigger>
                                    );
                                })}
                            </TabsList>
                            <Button 
                                variant="outline" 
                                size="sm"
                                onClick={openCreateCategoryModal}
                                className="ml-2"
                            >
                                <Plus className="h-4 w-4 mr-1" />
                                New Category
                            </Button>
                            </div>
                        </CardHeader>

                        <CardContent className="pt-6">
                            {categories.map(cat => (
                                <TabsContent key={cat.category_id} value={cat.category_id} className="mt-0">
                                    <CategoryAssetList
                                        category={getCategoryConfig(cat.category_id)}
                                        assets={getAssetsByCategory(cat.category_id)}
                                        isLoading={isLoading}
                                        onEdit={handleEdit}
                                        onDelete={setShowDeleteConfirm}
                                        onToggleActive={handleToggleActive}
                                        onPreview={setShowPreview}
                                        onEditCategory={() => openEditCategoryModal(cat)}
                                        onDeleteCategory={() => handleDeleteCategory(cat.category_id)}
                                    />
                                </TabsContent>
                            ))}
                            {categories.length === 0 && !isLoading && (
                                <div className="text-center py-12">
                                    <Layers className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                                    <p className="text-slate-500 mb-4">No categories found</p>
                                    <Button onClick={openCreateCategoryModal}>
                                        <Plus className="h-4 w-4 mr-1" />
                                        Create First Category
                                    </Button>
                                </div>
                            )}
                        </CardContent>
                    </Tabs>
                </Card>
            </div>

            {/* Create Modal */}
            <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>Add New Asset</DialogTitle>
                        <DialogDescription>
                            Create a new visual asset for the product configurator
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto min-h-0">
                        <AssetForm 
                            formData={formData} 
                            setFormData={setFormData}
                            isNew={true}
                            categories={categories}
                        />
                    </div>
                    <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
                        <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleCreate} disabled={isSaving}>
                            {isSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Creating...</> : 'Create Asset'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Modal */}
            <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
                <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
                    <DialogHeader className="flex-shrink-0">
                        <DialogTitle>Edit Asset</DialogTitle>
                        <DialogDescription>
                            Update the asset for {getCategoryConfig(editingAsset?.category_id || editingAsset?.category).label}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="flex-1 overflow-y-auto min-h-0">
                        <AssetForm 
                            formData={formData} 
                            setFormData={setFormData}
                            isNew={false}
                            categories={categories}
                        />
                    </div>
                    <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
                        <Button variant="outline" onClick={() => setShowEditModal(false)}>
                            Cancel
                        </Button>
                        <Button onClick={handleUpdate} disabled={isSaving}>
                            {isSaving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : 'Save Changes'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <Dialog open={!!showDeleteConfirm} onOpenChange={() => setShowDeleteConfirm(null)}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>Delete Asset</DialogTitle>
                    </DialogHeader>
                    <div className="py-4">
                        <p className="text-slate-600">
                            Are you sure you want to delete <strong>{showDeleteConfirm?.label}</strong>?
                        </p>
                        <p className="text-sm text-slate-400 mt-2">
                            This will remove it from the Visual Builder. Consider hiding it instead if you might need it later.
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDeleteConfirm(null)}>
                            Cancel
                        </Button>
                        <Button 
                            variant="destructive"
                            onClick={() => handleDelete(showDeleteConfirm?.id)}
                            disabled={isDeleting}
                        >
                            {isDeleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Deleting...</> : 'Delete'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Category Modal */}
            <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle>{editingCategory ? 'Edit Category' : 'Create New Category'}</DialogTitle>
                        <DialogDescription>
                            {editingCategory 
                                ? 'Update category details (category_id is immutable)'
                                : 'Define a new asset category for the product configurator'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="category_id">Category ID *</Label>
                                <Input
                                    id="category_id"
                                    value={categoryFormData.category_id}
                                    onChange={(e) => setCategoryFormData({...categoryFormData, category_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_')})}
                                    placeholder="e.g., vinyl_label"
                                    disabled={!!editingCategory}
                                    className={editingCategory ? 'bg-slate-100' : ''}
                                />
                                <p className="text-xs text-slate-500">Lowercase, underscores only. Cannot be changed later.</p>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="cat_label">Display Label *</Label>
                                <Input
                                    id="cat_label"
                                    value={categoryFormData.label}
                                    onChange={(e) => setCategoryFormData({...categoryFormData, label: e.target.value})}
                                    placeholder="e.g., Vinyl Label"
                                />
                            </div>
                        </div>
                        
                        <div className="space-y-2">
                            <Label htmlFor="cat_description">Description</Label>
                            <Input
                                id="cat_description"
                                value={categoryFormData.description}
                                onChange={(e) => setCategoryFormData({...categoryFormData, description: e.target.value})}
                                placeholder="Brief description of this category"
                            />
                        </div>
                        
                        <div className="grid grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="cat_icon">Icon</Label>
                                <Select
                                    value={categoryFormData.icon}
                                    onValueChange={(val) => setCategoryFormData({...categoryFormData, icon: val})}
                                >
                                    <SelectTrigger id="cat_icon">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="disc">Disc</SelectItem>
                                        <SelectItem value="package">Package</SelectItem>
                                        <SelectItem value="layers">Layers</SelectItem>
                                        <SelectItem value="palette">Palette</SelectItem>
                                        <SelectItem value="sparkles">Sparkles</SelectItem>
                                        <SelectItem value="plus">Plus</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="cat_ui_type">UI Type</Label>
                                <Select
                                    value={categoryFormData.ui_type}
                                    onValueChange={(val) => setCategoryFormData({...categoryFormData, ui_type: val})}
                                >
                                    <SelectTrigger id="cat_ui_type">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="cards">Cards</SelectItem>
                                        <SelectItem value="toggles">Toggles</SelectItem>
                                        <SelectItem value="dropdown">Dropdown</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="cat_order">Order</Label>
                                <Input
                                    id="cat_order"
                                    type="number"
                                    value={categoryFormData.display_order}
                                    onChange={(e) => setCategoryFormData({...categoryFormData, display_order: parseInt(e.target.value) || 0})}
                                />
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                            <Switch
                                id="cat_multi"
                                checked={categoryFormData.allow_multiple}
                                onCheckedChange={(checked) => setCategoryFormData({...categoryFormData, allow_multiple: checked})}
                            />
                            <Label htmlFor="cat_multi">Allow multiple selections</Label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowCategoryModal(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={editingCategory ? handleUpdateCategory : handleCreateCategory} 
                            disabled={isSaving}
                        >
                            {isSaving 
                                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> 
                                : editingCategory ? 'Save Changes' : 'Create Category'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Image Preview Modal */}
            <Dialog open={!!showPreview} onOpenChange={() => setShowPreview(null)}>
                <DialogContent className="max-w-3xl">
                    <DialogHeader>
                        <DialogTitle>{showPreview?.label}</DialogTitle>
                        <DialogDescription>
                            {showPreview?.short_description}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="py-4">
                        {showPreview?.image_url ? (
                            <div className="relative rounded-lg overflow-hidden bg-slate-100">
                                <img 
                                    src={showPreview.image_url} 
                                    alt={showPreview.label}
                                    className="w-full h-auto max-h-[400px] object-contain"
                                />
                            </div>
                        ) : (
                            <div className="h-48 bg-slate-100 rounded-lg flex items-center justify-center">
                                <p className="text-slate-400">No image</p>
                            </div>
                        )}
                        {showPreview?.why_it_matters && (
                            <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                                <p className="text-sm font-medium text-purple-800 mb-1">Why this matters</p>
                                <p className="text-sm text-purple-600">{showPreview.why_it_matters}</p>
                            </div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowPreview(null)}>
                            Close
                        </Button>
                        {showPreview?.image_url && (
                            <Button variant="outline" onClick={() => window.open(showPreview.image_url, '_blank')}>
                                <ExternalLink className="h-4 w-4 mr-2" />
                                Open Original
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

// Category Asset List Component
function CategoryAssetList({ category, assets, isLoading, onEdit, onDelete, onToggleActive, onPreview, onEditCategory, onDeleteCategory }) {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                <span className="ml-2 text-slate-500">Loading assets...</span>
            </div>
        );
    }

    if (assets.length === 0) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">{category.description}</p>
                    {onEditCategory && (
                        <div className="flex gap-2">
                            <Button variant="outline" size="sm" onClick={onEditCategory}>
                                <Edit className="h-4 w-4 mr-1" />
                                Edit Category
                            </Button>
                            {onDeleteCategory && category.asset_count === 0 && (
                                <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={onDeleteCategory}>
                                    <Trash2 className="h-4 w-4 mr-1" />
                                    Delete
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                <div className="text-center py-12">
                    <Image className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <h3 className="text-lg font-medium text-slate-900 mb-1">No assets in {category.label}</h3>
                    <p className="text-sm text-slate-500">Add assets to this category to use them in flows</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500">{category.description}</p>
                {onEditCategory && (
                    <Button variant="outline" size="sm" onClick={onEditCategory}>
                        <Edit className="h-4 w-4 mr-1" />
                        Edit Category
                    </Button>
                )}
            </div>
            
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead className="w-[60px]">Order</TableHead>
                        <TableHead className="w-[100px]">Preview</TableHead>
                        <TableHead className="w-[120px]">Option ID</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-[80px]">Status</TableHead>
                        <TableHead className="w-[100px]"></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {assets.map((asset, index) => (
                        <TableRow key={asset.id} className={!asset.is_active ? 'opacity-50' : ''}>
                            <TableCell>
                                <div className="flex items-center gap-1 text-slate-400">
                                    <GripVertical className="h-4 w-4" />
                                    <span className="text-sm">{asset.display_order}</span>
                                </div>
                            </TableCell>
                            <TableCell>
                                {asset.image_url ? (
                                    <button 
                                        onClick={() => onPreview(asset)}
                                        className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100 hover:ring-2 hover:ring-purple-400 transition-all"
                                    >
                                        <img 
                                            src={asset.image_url} 
                                            alt={asset.label}
                                            className="w-full h-full object-cover"
                                        />
                                    </button>
                                ) : (
                                    <div className="w-16 h-16 rounded-lg bg-slate-100 flex items-center justify-center">
                                        <Image className="h-6 w-6 text-slate-300" />
                                    </div>
                                )}
                            </TableCell>
                            <TableCell>
                                <code className="text-xs bg-slate-100 px-2 py-1 rounded">
                                    {asset.option_id}
                                </code>
                            </TableCell>
                            <TableCell>
                                <span className="font-medium">{asset.label}</span>
                            </TableCell>
                            <TableCell>
                                <span className="text-sm text-slate-500 line-clamp-2">
                                    {asset.short_description || '—'}
                                </span>
                            </TableCell>
                            <TableCell>
                                <button
                                    onClick={() => onToggleActive(asset)}
                                    className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                                        asset.is_active 
                                            ? 'bg-emerald-50 text-emerald-700' 
                                            : 'bg-slate-100 text-slate-500'
                                    }`}
                                >
                                    {asset.is_active ? (
                                        <><Eye className="h-3 w-3" /> Visible</>
                                    ) : (
                                        <><EyeOff className="h-3 w-3" /> Hidden</>
                                    )}
                                </button>
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center gap-1">
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => onEdit(asset)}
                                        data-testid={`edit-asset-${asset.id}`}
                                    >
                                        <Edit className="h-4 w-4 text-slate-400" />
                                    </Button>
                                    <Button 
                                        variant="ghost" 
                                        size="sm"
                                        className="h-8 w-8 p-0"
                                        onClick={() => onDelete(asset)}
                                    >
                                        <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-500" />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}

// Asset Form Component
function AssetForm({ formData, setFormData, isNew, categories = [] }) {
    const [previewError, setPreviewError] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);
    const fileInputRef = useState(null);

    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Invalid file type. Please use JPEG, PNG, WebP, or GIF.');
            return;
        }

        // Validate file size (5MB max)
        if (file.size > 5 * 1024 * 1024) {
            toast.error('File too large. Maximum size is 5MB.');
            return;
        }

        setIsUploading(true);
        setUploadProgress('Uploading...');

        try {
            const token = localStorage.getItem('token');
            const formDataUpload = new FormData();
            formDataUpload.append('file', file);

            const response = await axios.post(
                `${API}/api/visual-builder/admin/assets/upload-image`,
                formDataUpload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    },
                    onUploadProgress: (progressEvent) => {
                        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setUploadProgress(`Uploading... ${percent}%`);
                    }
                }
            );

            // Set the returned URL and s3_key
            setFormData(prev => ({ 
                ...prev, 
                image_url: response.data.image_url,
                s3_key: response.data.s3_key  // Store s3_key for URL refresh
            }));
            setPreviewError(false);
            toast.success('Image uploaded successfully');
        } catch (error) {
            console.error('Upload failed:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
            setIsUploading(false);
            setUploadProgress(null);
            // Reset file input
            event.target.value = '';
        }
    };

    return (
        <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
                {/* Category */}
                <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select 
                        value={formData.category_id} 
                        onValueChange={(v) => setFormData(prev => ({ ...prev, category_id: v }))}
                        disabled={!isNew}
                    >
                        <SelectTrigger>
                            <SelectValue placeholder="Select category..." />
                        </SelectTrigger>
                        <SelectContent>
                            {categories.map(cat => (
                                <SelectItem key={cat.category_id} value={cat.category_id}>
                                    {cat.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Option ID */}
                <div className="space-y-2">
                    <Label>Option ID *</Label>
                    <Input
                        value={formData.option_id}
                        onChange={(e) => setFormData(prev => ({ ...prev, option_id: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                        placeholder="e.g., black, 180g, gatefold"
                        disabled={!isNew}
                    />
                    <p className="text-xs text-slate-400">Unique identifier within category (lowercase, no spaces)</p>
                </div>
            </div>

            {/* Label */}
            <div className="space-y-2">
                <Label>Label *</Label>
                <Input
                    value={formData.label}
                    onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                    placeholder="e.g., Classic Black, Heavyweight (180g)"
                />
            </div>

            {/* Image - Upload or URL */}
            <div className="space-y-3">
                <Label>Image</Label>
                
                {/* Upload Section */}
                <div className="border-2 border-dashed border-border rounded-lg p-4 hover:border-purple-300 transition-colors">
                    <div className="flex items-center gap-4">
                        {/* Preview */}
                        <div className="w-20 h-20 rounded-lg overflow-hidden bg-slate-100 flex-shrink-0 border">
                            {formData.image_url && !previewError ? (
                                <img 
                                    src={formData.image_url} 
                                    alt="Preview"
                                    className="w-full h-full object-cover"
                                    onError={() => setPreviewError(true)}
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <Image className="h-8 w-8 text-slate-300" />
                                </div>
                            )}
                        </div>
                        
                        {/* Upload Button */}
                        <div className="flex-1">
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/webp,image/gif"
                                onChange={handleFileUpload}
                                className="hidden"
                                id="image-upload"
                                disabled={isUploading}
                            />
                            <label htmlFor="image-upload">
                                <Button 
                                    type="button" 
                                    variant="outline" 
                                    size="sm"
                                    disabled={isUploading}
                                    className="cursor-pointer"
                                    asChild
                                >
                                    <span>
                                        {isUploading ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                {uploadProgress || 'Uploading...'}
                                            </>
                                        ) : (
                                            <>
                                                <Upload className="h-4 w-4 mr-2" />
                                                Upload Image
                                            </>
                                        )}
                                    </span>
                                </Button>
                            </label>
                            <p className="text-xs text-slate-400 mt-1">
                                JPEG, PNG, WebP, or GIF. Max 5MB. <span className="text-purple-500 font-medium">Ideal: 400×400px</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Or use URL */}
                <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                        <span className="w-full border-t border-border" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                        <span className="bg-white px-2 text-slate-400">Or paste URL</span>
                    </div>
                </div>

                <div className="flex gap-2">
                    <Input
                        value={formData.image_url}
                        onChange={(e) => {
                            setFormData(prev => ({ ...prev, image_url: e.target.value }));
                            setPreviewError(false);
                        }}
                        placeholder="https://images.unsplash.com/..."
                        className="flex-1"
                        disabled={isUploading}
                    />
                    {formData.image_url && (
                        <Button 
                            type="button"
                            variant="ghost" 
                            size="sm"
                            onClick={() => {
                                setFormData(prev => ({ ...prev, image_url: '' }));
                                setPreviewError(false);
                            }}
                        >
                            Clear
                        </Button>
                    )}
                </div>
            </div>

            {/* Short Description */}
            <div className="space-y-2">
                <Label>Short Description</Label>
                <Input
                    value={formData.short_description}
                    onChange={(e) => setFormData(prev => ({ ...prev, short_description: e.target.value }))}
                    placeholder="e.g., Best value, our recommendation"
                />
                <p className="text-xs text-slate-400">Shown below the label in Visual Builder</p>
            </div>

            {/* Why It Matters */}
            <div className="space-y-2">
                <Label>Why It Matters</Label>
                <Textarea
                    value={formData.why_it_matters}
                    onChange={(e) => setFormData(prev => ({ ...prev, why_it_matters: e.target.value }))}
                    placeholder="The classic album cover. Clean, simple, professional."
                    rows={2}
                />
                <p className="text-xs text-slate-400">Educational copy explaining the option's value</p>
            </div>

            {/* Display Order and Active */}
            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                    <Label>Display Order</Label>
                    <Input
                        type="number"
                        value={formData.display_order}
                        onChange={(e) => setFormData(prev => ({ ...prev, display_order: parseInt(e.target.value) || 0 }))}
                        min={0}
                    />
                </div>
                <div className="space-y-2">
                    <Label>Visibility</Label>
                    <div className="flex items-center gap-3 pt-2">
                        <Switch
                            checked={formData.is_active}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                        />
                        <span className="text-sm text-slate-600">
                            {formData.is_active ? 'Visible to customers' : 'Hidden from customers'}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
